import { Link } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { contentFor, formatPrice, type Locale, type Product } from "@nh/shared";
import { Button } from "@/shared/ui/core";
import { ProductThumb } from "@/features/shop/components/ProductThumb";
import { numberLocaleOf, type ResolvedPrice } from "@/features/shop/shop.catalog";
import { maxQuantityFor } from "../cart.model";
import { QuantityStepper } from "./QuantityStepper";

export interface CartLineItemProps {
  product: Product;
  quantity: number;
  /** Resuelto del CATÁLOGO en cada render, nunca leído del almacenamiento. */
  price: ResolvedPrice | null;
  locale: Locale;
  onQuantityChange: (quantity: number) => void;
  onRemove: () => void;
  /** Se llama al pulsar el nombre: el panel se cierra antes de navegar. */
  onNavigate: () => void;
}

/**
 * Una fila del carrito.
 *
 * ── El precio se resuelve, no se recuerda ────────────────────────────
 * `price` llega ya resuelto contra el catálogo que acaba de traer el
 * servidor. Si el producto subió de precio desde que se metió al carrito,
 * esta fila enseña el precio NUEVO. Guardarlo en `localStorage` habría
 * dejado congelado el de aquel día, y el cobro habría sido otro.
 *
 * ── Dos números, no uno ──────────────────────────────────────────────
 * El unitario ("$1,299.00 c/u") y el de la línea. Con solo el total de la
 * línea no hay forma de comprobar si el "3" del selector es lo que se
 * quería; con solo el unitario, hay que multiplicar de cabeza.
 *
 * ── Cuando el precio viene en la otra moneda ─────────────────────────
 * `priceFor` cae a la otra moneda cuando el producto no se vende en la
 * activa. La fila lo DICE en vez de enseñar un símbolo de peso sobre una
 * cifra en dólares — y el subtotal deja esa línea fuera, porque sumar dos
 * monedas da un número inventado.
 */
export function CartLineItem({
  product,
  quantity,
  price,
  locale,
  onQuantityChange,
  onRemove,
  onNavigate,
}: CartLineItemProps) {
  const { t } = useTranslation();
  const content = contentFor(product.translations, locale);
  const numberLocale = numberLocaleOf(locale);
  const image = product.imageUrl || product.imageUrls?.[0];

  return (
    <li className="flex gap-4 py-4">
      <ProductThumb src={image} name={content.name} decorative />

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-2">
          {/* `min-w-0` es lo que impide que un nombre largo desborde su
              columna del flex y empuje el botón de quitar fuera del panel. */}
          <div className="min-w-0">
            <Link
              to={`/producto/${product.slug}`}
              onClick={onNavigate}
              className="line-clamp-2 rounded-ds-sm text-[14px] font-semibold leading-tight text-ds-ink transition hover:text-ds-violet-pop focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-violet-pop/50"
            >
              {content.name}
            </Link>

            {product.presentation && (
              <p className="mt-0.5 text-[12px] text-ds-ink-3">{product.presentation}</p>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            iconOnly
            leftIcon={<Trash2 />}
            onClick={onRemove}
            aria-label={t("Quitar {{producto}} del carrito", { producto: content.name })}
            className="shrink-0 border-0 text-ds-ink-3 hover:text-ds-terracotta"
          />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <QuantityStepper
            value={quantity}
            onChange={onQuantityChange}
            max={maxQuantityFor(product.stock)}
            label={content.name}
          />

          <div className="text-right">
            {price ? (
              <>
                <p className="text-[14px] font-semibold text-ds-ink">
                  {formatPrice(price.cents * quantity, price.currency, numberLocale)}
                </p>
                <p className="text-[11px] text-ds-ink-3">
                  {t("{{precio}} c/u", {
                    precio: formatPrice(price.cents, price.currency, numberLocale),
                  })}
                  {price.isFallbackCurrency && ` · ${price.currency}`}
                </p>
              </>
            ) : (
              <p className="text-[12px] font-medium text-ds-ink-3">
                {t("Precio a consultar")}
              </p>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
